import React, { useEffect, useState } from 'react';
import { Box, InputAdornment, SxProps, Typography } from '@mui/material';
import {
    Autocomplete,
    SearchIcon,
    TextField as MuiTextField,
    Tooltip,
} from '@biq/apex-ui';
import { Controller } from 'react-hook-form';
import { useDictionaryList } from '../../hooks';
import { getNormalizedDictionaryColors, validationsMessages } from '../../lib';
import { ChainListItem } from '../../pages/lifegraph-explorer/components/lifegraph-filter/ChainListItem';
import { autocompleteChainSelectorHelperStyles } from './autocomplete-chain-selector-helper';
import { ChainSvg } from '../../assets';

interface AutocompleteChainSelectorProps {
    tooltipTitle: string;
    handleOnChange?: (__: any, value: any) => void;
    error?: boolean;
    errorText?: string;
    autocompleteStyles?: SxProps;
    listBoxProps?: SxProps;
    selectedChain?: string;
    controlName: string;
    control: any;
    label?: string;
    hasSearchIcon?: boolean;
}

export const AutocompleteChainSelector = ({
    tooltipTitle,
    handleOnChange,
    error,
    errorText,
    autocompleteStyles,
    listBoxProps,
    selectedChain,
    controlName,
    control,
    label,
    hasSearchIcon = true,
}: AutocompleteChainSelectorProps): React.JSX.Element => {
    const { data: dictionaries } = useDictionaryList();
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const colors: Record<string, string> = getNormalizedDictionaryColors(
        dictionaries?.data ?? []
    );

    useEffect(() => {
        if (selectedChain) {
            if (
                inputRef.current &&
                inputRef.current.scrollWidth <= inputRef.current.clientWidth
            ) {
                setShowTooltip(false);
            }
        }
    }, [selectedChain, inputRef.current, setShowTooltip]);

    const setChainTooltip = (): void => {
        if (
            inputRef.current &&
            inputRef.current?.scrollWidth > inputRef.current?.clientWidth
        ) {
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const checkTextLengthForTooltip = (value: string): void => {
        if (value) {
            setChainTooltip();
        } else {
            setShowTooltip(false);
        }
    };

    const onMouseEnter = (): void => {
        checkTextLengthForTooltip(selectedChain ?? '');
    };

    const onMouseLeave = (): void => {
        setShowTooltip(false);
    };

    return (
        <Tooltip
            title={tooltipTitle}
            placement='top'
            arrow
            open={showTooltip}
            disableHoverListener={true}
            disableFocusListener={true}
            disableTouchListener={true}
        >
            <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                <Controller
                    name={controlName}
                    control={control}
                    render={({ field }): React.JSX.Element => (
                        <Autocomplete
                            {...field}
                            title={selectedChain}
                            value={selectedChain}
                            disableClearable={!selectedChain}
                            options={(dictionaries?.data ?? []).map(
                                (d) => d.name
                            )}
                            onChange={(__, value): void => {
                                field.onChange(value || '');
                                handleOnChange?.(__, value);
                            }}
                            noOptionsText={
                                <Box textAlign='center'>
                                    <img
                                        alt='chain-image'
                                        src={ChainSvg}
                                        width={130}
                                        height={70}
                                    />
                                    <Box>
                                        <Typography variant='h4'>
                                            No Results.
                                        </Typography>
                                        <Typography variant='body1'>
                                            Please Try Again.
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                            sx={autocompleteStyles}
                            ListboxProps={{
                                sx:
                                    listBoxProps ??
                                    autocompleteChainSelectorHelperStyles.listBoxProps,
                            }}
                            renderOption={(
                                optionProps,
                                option,
                                { index }
                            ): React.JSX.Element => {
                                return (
                                    <ChainListItem
                                        index={index}
                                        optionProps={optionProps}
                                        option={option}
                                        colors={colors}
                                    />
                                );
                            }}
                            renderInput={(params): React.JSX.Element => (
                                <MuiTextField
                                    {...params}
                                    size='medium'
                                    variant='outlined'
                                    label={label}
                                    required={true}
                                    error={error}
                                    errorText={
                                        errorText ??
                                        validationsMessages.required
                                    }
                                    inputRef={inputRef}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: hasSearchIcon
                                            ? [
                                                  <InputAdornment
                                                      key='searchIcon'
                                                      position='start'
                                                  >
                                                      <SearchIcon />
                                                  </InputAdornment>,
                                              ]
                                            : params.InputProps.startAdornment,
                                    }}
                                />
                            )}
                        />
                    )}
                />
            </Box>
        </Tooltip>
    );
};
